import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ThemeProvider } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Hooks
import { useSafeArea, getSafeAreaStyles } from './hooks/useSafeArea';

// Context
import { BookMyOpAuthProvider, useBookMyOpAuth } from './contexts/BookMyOpAuthContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FavoritesProvider } from './contexts/FavoritesContext';

// Services
import locationService from './services/locationService';
import BookMyOpAPI from './services/BookMyOpapi';

// Components
import SplashScreen from './components/SplashScreen';
import AuthSplashScreen from './components/AuthSplashScreen';
import MobileRouteGuard from './components/MobileRouteGuard';

import StatusBarHandler from './components/StatusBarHandler';
import NameRequiredDialog from './components/NameRequiredDialog';

// Layouts
import MainLayout from './pages/MainLayout';
import MobileLayout from './pages/MobileLayout';

// Pages
import BookmyOpHome from './pages/BookmyOpHome';
import BookmyopLogin from './pages/BookmyopLogin';
import Register from './pages/Register';
import FindDoctors from './pages/FindDoctors';
import DoctorDetail from './pages/DoctorDetail';
import HospitalDetails from './pages/HospitalDetails';
import HospitalList from './pages/HospitalList';
import ClinicList from './pages/ClinicList';
import BookmyopServiceProviders from './pages/BookmyopServiceProviders';
import BookmyopAppointments from './pages/BookmyopAppointments';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import IconDemo from './components/IconDemo';
import SafeAreaTest from './components/SafeAreaTest';
import Communication from './pages/Communication';
import CommunicationDetail from './pages/CommunicationDetail';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import NotificationsPage from './pages/NotificationsPage';

// Protected Appointments Page Component
const ProtectedAppointments = () => {
  const { isAuthenticated, isLoading } = useBookMyOpAuth();
  
  if (isLoading) {
    return <AuthSplashScreen />;
  }
  
  return isAuthenticated ? <BookmyopAppointments /> : <Navigate to="/login" />;
};

// Conditional Layout Component
const ConditionalLayout = () => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return isMobile ? <MobileLayout /> : <MainLayout />;
};

// Location Initializer Component - Runs after authentication is ready
const LocationInitializer = () => {
  const { isAuthenticated, userLocation, user, loading } = useBookMyOpAuth();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const initializeLocation = async () => {
      try {
        if (isAuthenticated) {
          // For authenticated users: ALWAYS set session location from profile data on page load
          // Backend requires session location even for authenticated users
          // Don't check sessionStorage flag - always set it from profile data
          console.log('🔍 LocationInitializer: User is authenticated, checking for location data...');
          
          let latitude = null;
          let longitude = null;
          let address = null;
          let city = null;
          let state = null;
          let pincode = null;
          
          // Try to get location from user object (from context)
          if (user) {
            latitude = user.latitude || user.current_latitude;
            longitude = user.longitude || user.current_longitude;
            address = user.address;
            city = user.city || user.current_city;
            state = user.state || user.current_state;
            pincode = user.pincode || user.current_pincode;
            console.log('🔍 LocationInitializer: Got location from user context:', { latitude, longitude, city, state });
          }
          
          // Fallback: Read from storage (localStorage on web, Preferences on mobile)
          // On mobile, localStorage is cleared when app closes, so check Preferences
          if (!latitude || !longitude) {
            try {
              let userDataStr = null;
              
              if (isNative) {
                // On mobile: Check localStorage first (might be restored from Preferences by auth context)
                userDataStr = localStorage.getItem('bookmyop_user_data');
                if (!userDataStr) {
                  // Try Preferences directly (async) - this is what persists across app restarts
                  const { Preferences } = require('@capacitor/preferences');
                  const prefResult = await Preferences.get({ key: 'bookmyop_user_data' });
                  userDataStr = prefResult.value;
                  if (userDataStr) {
                    // Also restore to localStorage for faster future access
                    localStorage.setItem('bookmyop_user_data', userDataStr);
                    console.log('📱 LocationInitializer: Restored user data from Preferences');
                  }
                }
              } else {
                // On web: Use localStorage directly
                userDataStr = localStorage.getItem('bookmyop_user_data');
              }
              
              if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                latitude = latitude || userData.latitude || userData.current_latitude;
                longitude = longitude || userData.longitude || userData.current_longitude;
                address = address || userData.address;
                city = city || userData.city || userData.current_city;
                state = state || userData.state || userData.current_state;
                pincode = pincode || userData.pincode || userData.current_pincode;
                console.log('🔍 LocationInitializer: Got location from storage:', { 
                  latitude, 
                  longitude, 
                  city, 
                  state,
                  source: isNative ? 'Preferences/localStorage' : 'localStorage'
                });
              }
            } catch (err) {
              console.error('❌ Error reading user data from storage:', err);
            }
          }
          
          if (latitude && longitude) {
            // Construct address if missing (backend requires address field)
            let finalAddress = address;
            if (!finalAddress) {
              // Build address from city and state
              const addressParts = [];
              if (city) addressParts.push(city);
              if (state) addressParts.push(state);
              if (pincode) addressParts.push(pincode);
              finalAddress = addressParts.length > 0 
                ? addressParts.join(', ') 
                : `${latitude}, ${longitude}`; // Fallback to coordinates
            }
            
            // Handle string values
            const latNum = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
            const lngNum = typeof longitude === 'string' ? parseFloat(longitude) : longitude;
            
            if (isNaN(latNum) || isNaN(lngNum)) {
              console.error('❌ LocationInitializer: Invalid coordinates:', { latitude, longitude });
              return;
            }
            
            console.log('📍 LocationInitializer: Setting session location from user profile:', { 
              latitude: latNum, 
              longitude: lngNum, 
              address: finalAddress,
              city, 
              state,
              url: '/api/bookmyop/session-location/'
            });
            try {
              // Set session location from profile data
              // Address is required by backend, so we always provide it
              // CRITICAL: This must complete before any API calls are made
              const response = await BookMyOpAPI.setSessionLocation({
                latitude: latNum,
                longitude: lngNum,
                address: finalAddress, // Always provide address (required field)
                city: city || null,
                state: state || null,
                pincode: pincode || null
              });
              sessionStorage.setItem('session_location_set', 'true');
              console.log('✅ LocationInitializer: Session location set successfully:', response);
            } catch (error) {
              console.error('❌ LocationInitializer: Failed to set session location:', error);
              console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: error.config?.url
              });
              // Don't throw - allow app to continue, but API calls might fail
            }
          } else {
            console.warn('⚠️ LocationInitializer: User profile has no location data');
          }
          return;
        }

        // For guest users: Check if location already set in this session
        const locationAlreadySet = sessionStorage.getItem('session_location_set');
        if (locationAlreadySet) {
          console.log('📍 Session location already set for guest, skipping auto-request');
          return;
        }

        // For guest users: Auto-detect location if permission granted
        // Check permission first - only auto-request if already granted
        const permission = await locationService.checkLocationPermission();
        
        if (permission === 'granted') {
          console.log('📍 Location permission already granted, setting session location...');
          await locationService.requestAndSetLocation();
          sessionStorage.setItem('session_location_set', 'true');
          console.log('✅ Session location initialized successfully');
        } else {
          console.log('📍 Location permission not granted, skipping auto-request');
        }
      } catch (error) {
        console.warn('⚠️ Failed to initialize session location:', error.message);
        // Don't show error to user - this is a background operation
      }
    };

    // Wait for auth to be fully initialized before setting location
    // Don't run if auth is still loading
    if (loading) {
      console.log('📍 LocationInitializer: Auth still loading, waiting...');
      return;
    }

    // CRITICAL: For authenticated users, set session location IMMEDIATELY (no delay)
    // API calls happen immediately on page load, so location must be set synchronously
    // For guest users, small delay is OK
    if (isAuthenticated) {
      // Run immediately for authenticated users - location data is already in localStorage
      console.log('📍 LocationInitializer: Auth ready, setting location immediately...', {
        isAuthenticated,
        hasUser: !!user,
        hasUserLocation: !!userLocation,
        loading
      });
      initializeLocation();
    } else {
      // For guest users, small delay is OK
      const timer = setTimeout(() => {
        console.log('📍 LocationInitializer: Starting initialization for guest...');
        initializeLocation();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, userLocation, user, loading]);

  return null; // This component doesn't render anything
};

// App Content Component
const AppContent = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    // Simulate different loading stages
    const loadingStages = [
      { progress: 10, message: 'Initializing app...', delay: 300 },
      { progress: 25, message: 'Loading components...', delay: 400 },
      { progress: 40, message: 'Setting up routing...', delay: 300 },
      { progress: 55, message: 'Preparing UI components...', delay: 400 },
      { progress: 70, message: 'Loading theme and styles...', delay: 300 },
      { progress: 85, message: 'Finalizing setup...', delay: 400 },
      { progress: 100, message: 'Ready!', delay: 200 }
    ];

    let currentStage = 0;

    const updateProgress = () => {
      if (currentStage < loadingStages.length) {
        const stage = loadingStages[currentStage];
        setLoadingProgress(stage.progress);
        
        setTimeout(() => {
          currentStage++;
          if (currentStage < loadingStages.length) {
            updateProgress();
          } else {
            // Final stage - complete loading
            setTimeout(() => {
              setIsInitializing(false);
            }, 500);
          }
        }, stage.delay);
      }
    };

    updateProgress();
  }, []);

  // Note: Session storage persists during navigation and only clears on browser close
  // No need to manually clear it

  if (isInitializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SplashScreen 
          // message={loadingProgress < 100 ? 'Loading BookMyOp...' : 'Welcome to BookMyOp!'}
          progress={loadingProgress}
          onComplete={() => setIsInitializing(false)}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3}>
          <BookMyOpAuthProvider>
            <FavoritesProvider>
              <NotificationProvider>
                <Router>
                {/* Initialize session location for guest users */}
                <LocationInitializer />
                
                <NameRequiredDialog />
                <Routes>
                {/* Auth Routes (without layout) */}
                <Route path="/login" element={<BookmyopLogin />} />
                <Route path="/register" element={<Register />} />
                
                {/* Routes with Conditional Layout (MobileLayout for mobile, MainLayout for desktop) */}
                <Route path="/" element={
                  <MobileRouteGuard>
                    <ConditionalLayout />
                  </MobileRouteGuard>
                }>
                  <Route index element={<BookmyOpHome />} />
                  <Route path="dashboard" element={<BookmyOpHome />} />
                  <Route path="find-doctors" element={<FindDoctors />} />
                  <Route path="find-doctors/clinics" element={<FindDoctors />} />
                  <Route path="find-doctors/hospitals" element={<FindDoctors />} />
                  <Route path="find-doctors/saved" element={<FindDoctors />} />
                  <Route path="doctors/:id" element={<DoctorDetail />} />
                  <Route path="hospitals" element={<HospitalList />} />
                  <Route path="hospitals/:id" element={<HospitalDetails />} />
                  <Route path="clinics" element={<ClinicList />} />
                  <Route path="service-providers" element={<BookmyopServiceProviders />} />
                  <Route path="support" element={<Communication />} />
                  <Route path="support/:id" element={<CommunicationDetail />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="delete-account" element={<DeleteAccount />} />
                  <Route path="appointments" element={<BookmyopAppointments />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="icons" element={<IconDemo />} />
                  <Route path="safe-area-test" element={<SafeAreaTest />} />
                </Route>
              </Routes>
              </Router>

              </NotificationProvider>
            </FavoritesProvider>
          </BookMyOpAuthProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

function App() {
  const safeAreaInsets = useSafeArea();
  
  return (
    <div 
      className="app-container" 
      style={getSafeAreaStyles(safeAreaInsets)}
    >
      <StatusBarHandler />
      <CustomThemeProvider>
        <AppContent />
      </CustomThemeProvider>
    </div>
  );
}

export default App; 